'use client';

import { useState } from 'react';
import {
  trackApySavingsCalculatorCalculate,
  trackApySavingsCalculatorReset,
} from 'components/analytics/eventHandlerTracking';
import { CalculatorShell } from 'components/Widget/shared/CalculatorShell';
import { useResultScroll } from 'components/Widget/shared/useResultScroll';
import { deferTracking } from 'components/Widget/shared/deferTracking';
import { formatAmount, parseCurrency } from 'components/Widget/shared/currency';
import { parsePercent } from 'components/Widget/shared/percent';
import { useCurrencyFields } from 'components/Widget/shared/useCurrencyFields';
import { usePercentFields } from 'components/Widget/shared/usePercentFields';
import { withPlaceholders } from 'components/Widget/shared/types';
import type { CalculatorFieldErrors, CalculatorFieldValues } from 'components/Widget/shared/types';
import { calculateApySavings } from './apySavings';
import { ApySavingsCalculatorForm } from './ApySavingsCalculatorForm';
import { ApySavingsCalculatorResult } from './ApySavingsCalculatorResult';
import { ApySavingsCalculatorSummary } from './ApySavingsCalculatorSummary';
import {
  APY_ERROR,
  APY_FIELD,
  DEFAULT_CONTRIBUTION_FREQUENCY,
  DEFAULT_SAVINGS_PERIOD,
  DISCLAIMER,
  getContributionFrequency,
  getSavingsPeriod,
  INITIAL_APY_VALUES,
  INITIAL_CURRENCY_VALUES,
  MAX_APY,
  MAX_FIELD_VALUE,
  MIN_RECURRING_CONTRIBUTION,
  RECURRING_CONTRIBUTION_ERROR,
  RECURRING_CONTRIBUTION_FIELD,
  STARTING_BALANCE_ERROR,
  STARTING_BALANCE_FIELD,
  TITLE,
} from './constants';
import type { ApySavingsState, ContributionFrequencyKey, SavingsPeriodKey } from './types';

const INITIAL_STATE: ApySavingsState = { errors: {}, panel: null };

const isOutsideRange = (raw: string, minimum: number, maximum: number): boolean => {
  // Sanitization strips the minus sign, so an out-of-range low value can only be an empty or small one.
  if (raw.trim() === '') return true;

  const value = parseCurrency(raw);

  return value < minimum || value > maximum;
};

const validate = (currencyValues: CalculatorFieldValues, apyValues: CalculatorFieldValues): CalculatorFieldErrors => {
  const errors: CalculatorFieldErrors = {};
  const apyRaw = apyValues[APY_FIELD.label];

  if (isOutsideRange(currencyValues[STARTING_BALANCE_FIELD.label], 0, MAX_FIELD_VALUE)) {
    errors[STARTING_BALANCE_FIELD.label] = STARTING_BALANCE_ERROR;
  }

  if (isOutsideRange(currencyValues[RECURRING_CONTRIBUTION_FIELD.label], MIN_RECURRING_CONTRIBUTION, MAX_FIELD_VALUE)) {
    errors[RECURRING_CONTRIBUTION_FIELD.label] = RECURRING_CONTRIBUTION_ERROR;
  }

  if (apyRaw.trim() === '' || parsePercent(apyRaw) > MAX_APY) errors[APY_FIELD.label] = APY_ERROR;

  return errors;
};

export const ApySavingsCalculator = () => {
  const [state, setState] = useState<ApySavingsState>(INITIAL_STATE);
  const [contributionFrequency, setContributionFrequency] =
    useState<ContributionFrequencyKey>(DEFAULT_CONTRIBUTION_FREQUENCY);
  const [savingsPeriod, setSavingsPeriod] = useState<SavingsPeriodKey>(DEFAULT_SAVINGS_PERIOD);
  // The fields already show a `$`, so blur must not format a second one in.
  const currency = useCurrencyFields(INITIAL_CURRENCY_VALUES, formatAmount);
  const apy = usePercentFields(INITIAL_APY_VALUES);
  const { resultsRef, scrollToResults } = useResultScroll();

  const handleCalculate = () => {
    // An untouched field calculates on the greyed default the user can see, rather than failing validation.
    const currencyValues = withPlaceholders(
      currency.normalize(),
      [STARTING_BALANCE_FIELD, RECURRING_CONTRIBUTION_FIELD],
      currency.isTouched,
    );
    const apyValues = withPlaceholders(apy.normalize(), [APY_FIELD], apy.isTouched);
    const errors = validate(currencyValues, apyValues);
    const period = getSavingsPeriod(savingsPeriod);

    // The form stays mounted beside the results, so a field still being edited keeps its unformatted digits.
    currency.commit();
    apy.commit();

    if (Object.keys(errors).length > 0) {
      // A failed Calculate raises the field errors and leaves any result already on screen alone.
      setState(previous => ({ ...previous, errors }));
    } else {
      const result = calculateApySavings({
        startingBalance: currencyValues[STARTING_BALANCE_FIELD.label],
        recurringContribution: currencyValues[RECURRING_CONTRIBUTION_FIELD.label],
        contributionFrequency,
        savingsPeriod,
        apy: apyValues[APY_FIELD.label],
      });

      setState({ errors: {}, panel: { periodLabel: period.label, result } });
      scrollToResults();
    }

    // The press is what is counted, so an attempt validation turned back still lands in the funnel.
    deferTracking(() => {
      trackApySavingsCalculatorCalculate({
        label: period.label,
        item_value: getContributionFrequency(contributionFrequency).label,
      });
    });
  };

  const handleReset = () => {
    setState(INITIAL_STATE);
    setContributionFrequency(DEFAULT_CONTRIBUTION_FREQUENCY);
    setSavingsPeriod(DEFAULT_SAVINGS_PERIOD);
    currency.reset();
    apy.reset();

    deferTracking(() => {
      trackApySavingsCalculatorReset();
    });
  };

  return (
    <CalculatorShell
      title={TITLE}
      disclaimer={DISCLAIMER}
      collapsibleDisclaimer
      summary={
        state.panel && <ApySavingsCalculatorSummary result={state.panel.result} periodLabel={state.panel.periodLabel} />
      }
      resultsRef={resultsRef}
      // Passed even while empty so the second column is reserved and the form does not shift on Calculate.
      results={
        state.panel && <ApySavingsCalculatorResult result={state.panel.result} periodLabel={state.panel.periodLabel} />
      }>
      <ApySavingsCalculatorForm
        errors={state.errors}
        currencyFieldProps={currency.fieldProps}
        apyFieldProps={apy.fieldProps}
        contributionFrequency={contributionFrequency}
        savingsPeriod={savingsPeriod}
        onContributionFrequencyChange={setContributionFrequency}
        onSavingsPeriodChange={setSavingsPeriod}
        onCalculate={handleCalculate}
        onReset={handleReset}
      />
    </CalculatorShell>
  );
};
